<template>
  <router-link class="router-link" :to="{path: `/share`, query: {room: room.id}}">
    <v-card
      class="room-card mt-4"
      :room-id="room.id"
      :class="{'room-active': room.id === activeRoomId}"
    >
      <v-card-title>
        <div class="d-flex w-100">
          <div class="flex text-truncate">
            <div class="d-flex align-center">
              <v-tooltip v-if="room.id === activeRoomId" top :disabled="isTouchDevice">
                <template #activator="{on}">
                  <v-icon color="accent" class="mr-2" v-on="on">far fa-signal-stream</v-icon>
                </template>
                <span>Raum aktiv</span>
              </v-tooltip>
              <span class="text-truncate" :title="room.name">{{room.name}}</span>
            </div>
          </div>

          <v-tooltip top :disabled="isTouchDevice">
            <template #activator="{on}">
              <v-btn icon v-on="on"><v-icon>far fa-external-link-square</v-icon></v-btn>
            </template>
            <span>Raum öffnen</span>
          </v-tooltip>

          <v-menu
            v-if="!isLocalRoom"
            close-on-click
            left
          >
            <template #activator="{on}">
              <v-btn icon v-on="on" @click.prevent.stop>
                <v-icon>far fa-ellipsis-v</v-icon>
              </v-btn>
            </template>
            <v-list>
              <v-list-item v-if="canInviteUsers" @click="_inviteUser">
                <v-list-item-title>Benutzer einladen</v-list-item-title>
              </v-list-item>
              <v-list-item two-line :disabled="!notifications" @click="_toggleMuted">
                <v-list-item-content>
                  <v-list-item-title>
                    Benachrichtigungen
                    <template v-if="isMutedRoom">aktivieren</template>
                    <template v-else>deaktivieren</template>
                  </v-list-item-title>
                  <v-list-item-subtitle v-if="!notifications">
                    Alle Benachrichtigungen deakitviert
                  </v-list-item-subtitle>
                </v-list-item-content>
                <v-list-item-action v-if="!notifications">
                  <v-btn icon style="pointer-events: all;" to="/share/notifications">
                    <v-icon>far fa-user-cog</v-icon>
                  </v-btn>
                </v-list-item-action>
              </v-list-item>
              <v-list-item v-if="room.owner.isCurrentUser" :to="`/share/rooms/update/${room.id}`">
                <v-list-item-title>Bearbeiten</v-list-item-title>
              </v-list-item>
              <v-list-item v-if="!room.owner.isCurrentUser" @click="_roomLeave">
                <v-list-item-title>Verlassen</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </div>
      </v-card-title>

      <v-card-subtitle class="d-flex">
        <div class="flex">
          <i v-if="room.description" class="description d-block">{{room.description}}</i>
          <div v-if="isLocalRoom">
            {{participants.length}} Benutzer online
          </div>
          <div v-if="!isLocalRoom">
            {{participants_onlineCount}} von {{participants.length}} Benutzer online<br>
            Erstellt von <i>{{room.owner.isCurrentUser ? 'dir' : room.owner.name}}</i>
            <v-tooltip v-if="room.private === true" top>
              <template #activator="{on}">
                <v-icon
                  class="icon-private ml-2"
                  v-on="on"
                  @click.prevent.stop
                >far fa-lock</v-icon>
              </template>
              <span>Privater Raum</span>
            </v-tooltip>
          </div>
        </div>
        <v-tooltip v-if="notifications && isMutedRoom" top :disabled="isTouchDevice">
          <template #activator="{on}">
            <v-icon class="mr-2" color="grey" v-on="on">fas fa-volume-mute</v-icon>
          </template>
          <span>Benachrichtigungen deaktiviert</span>
        </v-tooltip>
      </v-card-subtitle>

      <v-card-text class="participants d-flex align-center">
        <v-tooltip v-for="(participant, index) in participants_visible" :key="index" top>
          <template #activator="{on}">
            <div
              :style="'z-index:' + ((index - participants_visible.length )* -1)"
              :class="{
                'participant-online': participant.online,
                'participant-offline': !participant.online
              }"
              class="participant d-flex align-center justify-center"
              v-on="on"
              @click.prevent.stop
            >{{initials(participant.name)}}</div>
          </template>
          <span>
            {{participant.name}}
            <span v-if="participant.isCurrentUser">
              (Du)
            </span>
            <span
              :class="{'participant-online-status-online': participant.online}"
              class="participant-online-status"
            >{{participant.online ? 'online' : 'offline'}}</span>
          </span>
        </v-tooltip>

        <v-tooltip v-if="participants_more.length" top>
          <template #activator="{on}">
            <span class="ml-1" v-on="on" @click.prevent.stop>+ {{participants_more.length}}</span>
          </template>
          <span v-for="(participant, index) in participants_more" :key="index">
            {{participant.name}}
            <span v-if="participant.isCurrentUser">
              (Du)
            </span>
            <span
              :class="{'participant-online-status-online': participant.online}"
              class="participant-online-status"
            >{{participant.online ? 'online' : 'offline'}}</span><br>
          </span>
        </v-tooltip>
      </v-card-text>
    </v-card>
  </router-link>
</template>

<script>
  import eventBus from '../../assets/js/event-bus';
  import {isTouchDevice, canInviteUsers, compareAlphabetically, participantInitials} from '../../assets/js/utils';
  import {prompt} from '../Prompt';


  export default {
    name: 'Room',

    props: {
      room: {
        type: Object,
        default: () => ({})
      }
    },

    data() {
      return {
        isTouchDevice,
        participants_visible_count: 7
      };
    },

    computed: {
      isLocalRoom() {
        return this.room.id === 'local';
      },

      activeRoomId() {
        const room = this.$store.getters['share/getRoom'];
        return room ? room.id : room;
      },

      user() {
        return this.$store.getters['session/getUser'];
      },

      notifications() {
        const notifications = this.user?.preferences?.notifications;
        return notifications === false ? false : true;
      },

      mutedRooms() {
        return this.user?.preferences?.mutedRooms || [];
      },

      isMutedRoom() {
        return !!this.room && this.mutedRooms.includes(this.room.id);
      },

      canInviteUsers() {
        return canInviteUsers(this.room);
      },

      participants() {
        const {room} = this;
        let participants = [...room.participants];
        if (room.owner) {
          participants.push(room.owner);
        }

        participants.sort((partA, partB) => {
          // Sort current user to top.
          if (partA.isCurrentUser) {
            return -1;
          }
          if (partB.isCurrentUser) {
            return 1;
          }
          // Sort by online state (online first).
          if (partA.online && !partB.online) {
            return -1;
          }
          if (!partA.online && partB.online) {
            return 1;
          }
          // Both have the same online state -> sort alphabetically.
          return compareAlphabetically(partA.name, partB.name);
        });

        return participants;
      },

      participants_visible() {
        return this.participants.slice(0, this.participants_visible_count);
      },

      participants_more() {
        const {participants, participants_visible_count} = this;
        const more = participants.length - participants_visible_count;
        if (more > 0) {
          return participants.slice(participants_visible_count, participants_visible_count + more);
        }
        return [];
      },

      participants_onlineCount() {
        return this.participants.filter((participant) => participant.online).length;
      }
    },

    methods: {
      initials(participant) {
        return participantInitials(participant);
      },

      _inviteUser() {
        eventBus.$emit('roomInviteUser', this.room);
      },

      _toggleMuted() {
        if (!this.notifications) {
          return;
        }

        const {room, mutedRooms, isMutedRoom} = this;
        if (isMutedRoom) {
          const index = mutedRooms.indexOf(room.id);
          mutedRooms.splice(index, 1);
          this._setPreferences({mutedRooms});

        } else {
          this._setPreferences({mutedRooms: [...mutedRooms, room.id]});
        }
      },

      async _roomLeave() {
        const {room} = this;

        try {
          await prompt(`Raum "${room.name}" verlassen?`);

          await this.$api.post('RoomLeave', {id: this.room.id});

          if (room.id === this.activeRoomId) {
            // leave room
            this.$router.replace({path: '/share/rooms', query: {room: 'local'}});
          }

        } catch (error) {
          /* NOOP */
        }
      },

      async _setPreferences(preferences) {
        try {
          await this.$api.post('UserSetPreferences', preferences);
          this.$auth.verifyUser(); // Updates the user.
        } catch (error) {
          eventBus.$emit('notifyUser', 'Einstellungen könnten nicht gespeichert werden');
        }
      }
    }
  };
</script>

<style scoped>
  a {
    text-decoration: none;
  }

  .v-list-item {
    cursor: pointer;
  }
</style>
